import React, { useState } from 'react';
import { SwipeEventData, useSwipeable } from 'react-swipeable';
import styled from 'styled-components';
import Icon from '@99designs/frontend-components/components/Icon/Icon';
import Box from '../../../../../shared/components/Box';
import { colors, media, spaces } from '../../../../../shared/lib/styles';
import { WinningEntryType } from '../../ContestOverviewData';
import ClientReview from '../ClientReview';
import WinnerCard from '../WinnerCard';
import MultipleWinnersCarouselItem from './MultipleWinnersCarouselItem';

interface Props {
    winningEntries: WinningEntryType[];
    Faqs: React.ReactNode;
}

const triggerSize = '120px';
const indicatorSize = '10px';

const Carousel = styled.div`
    position: relative;
`;

const Trigger = styled.button`
    position: absolute;
    z-index: 2;
    top: 310px;
    width: ${triggerSize};
    height: ${triggerSize};
    background-color: ${colors.heritage};
    color: ${colors.white};
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    outline: 0;

    &:hover {
        background-color: colors.heritage;
    }

    @media ${media('small')} {
        display: none;
    }
`;

const NextTrigger = styled(Trigger)`
    right: 0;
`;

const PrevTrigger = styled(Trigger)`
    left: 0;
`;

const Indicators = styled.div`
    text-align: left;
    margin-top: ${spaces.gutter}px;
    margin-bottom: ${spaces.gutter}px;
    line-height: 0;

    @media ${media('small', 'medium')} {
        text-align: center;
    }
`;

const Indicator = styled.div`
    display: inline-block;
    width: ${indicatorSize};
    height: ${indicatorSize};
    background: ${colors.grey500};
    border-radius: ${spaces.space8}px;
    cursor: pointer;

    &:not(:first-child) {
        margin-left: ${spaces.halfGutter}px;
    }

    &[data-active] {
        background: ${colors.grey700};
    }
`;

export default function MultipleWinnersCarousel({ winningEntries, Faqs }: Props) {
    const lastStep = winningEntries.length - 1;

    const goToNextStep = () => {
        if (activeStep == lastStep) {
            setActiveStep(0);
        } else {
            setActiveStep(activeStep + 1);
        }
    };

    const goToPrevStep = () => {
        if (activeStep == 0) {
            setActiveStep(lastStep);
        } else {
            setActiveStep(activeStep - 1);
        }
    };

    function handleSwipe(eventData: SwipeEventData) {
        const elementType = (eventData.event.target as Element).tagName.toLowerCase();

        if (elementType === 'img') {
            return;
        }

        if (eventData.dir === 'Left') {
            goToNextStep();
        }
        if (eventData.dir === 'Right') {
            goToPrevStep();
        }
    }

    const [activeStep, setActiveStep] = useState(0);

    const swipeHandlers = useSwipeable({
        onSwipedLeft: handleSwipe,
        onSwipedRight: handleSwipe,
        preventDefaultTouchmoveEvent: true,
        trackMouse: false,
        trackTouch: true,
    });

    const winningEntry = winningEntries[activeStep];

    if (winningEntries.length == 0) {
        return null;
    }

    return (
        <Carousel {...swipeHandlers}>
            <div className="row">
                <div className="column small-12 medium-8 medium-offset-2 normal-12 large-12">
                    <div className="row">
                        <div
                            className="column small-12 medium-12 normal-5 normal-offset-1 large-4 large-offset-2"
                            style={{ textAlign: 'center' }} // TODO: Replace with Box component
                        >
                            <Box paddingRight={['none', 'none', 'space10', 'space10']}>
                                <MultipleWinnersCarouselItem activeStep={activeStep}>
                                    <WinnerCard winningEntry={winningEntry} />
                                </MultipleWinnersCarouselItem>
                            </Box>
                        </div>
                        <div className="column small-12 medium-12 normal-6 large-6">
                            <MultipleWinnersCarouselItem activeStep={activeStep}>
                                <ClientReview review={winningEntry.designer.topReview} />
                            </MultipleWinnersCarouselItem>

                            <Indicators>
                                {winningEntries.length > 1 &&
                                    winningEntries.map((_w, index) => {
                                        return (
                                            <Indicator
                                                key={`multiple-winners-carousel-indicator-${index}`}
                                                role="option"
                                                aria-selected={index == activeStep}
                                                data-active={index == activeStep ? '' : undefined}
                                                onClick={() => setActiveStep(index)}
                                            />
                                        );
                                    })}
                            </Indicators>
                            {Faqs}
                        </div>
                    </div>
                </div>
            </div>
            {winningEntries.length > 1 && (
                <MultipleWinnersCarouselTriggers
                    handlePrevClick={goToPrevStep}
                    handleNextClick={goToNextStep}
                />
            )}
        </Carousel>
    );
}

interface TriggersProps {
    handlePrevClick: () => void;
    handleNextClick: () => void;
}

const MultipleWinnersCarouselTriggers = ({ handlePrevClick, handleNextClick }: TriggersProps) => {
    return (
        <>
            <PrevTrigger aria-label="Previous" onClick={handlePrevClick}>
                <Icon type="left-4" variations={['2x']} />
            </PrevTrigger>
            <NextTrigger aria-label="Next" onClick={handleNextClick}>
                <Icon type="right-4" variations={['2x']} />
            </NextTrigger>
        </>
    );
};
